import type { Package, PackageDefinition } from '@/hooks/use-software-update-packages';
import { API_ERROR_CODES, getAPIErrorMessage, getAPIProblem } from '@/lib/api';
import type { PackageInUseConflict } from '@/lib/software-update-package.service';

export const MACHINE_TYPES = ['rower', 'bike', 'treadmill'] as const;
export const ATS_IDS = ['ats1', 'ats2', 'ats5', 'ats6', 'atsa'] as const;

/** A package row with its resolved definition attached for display. */
export type RowPackage = Package & {
  definition?: PackageDefinition;
};

export function packageKey(type: string, packageName: string) {
  return `${type}:${packageName}`;
}

/** Package key for anything carrying a package `type` + `package_name` (package, slot, rollout package). */
export function packageKeyOf(item: { type: string; package_name: string }) {
  return packageKey(item.type, item.package_name);
}

/**
 * Resolves the human-facing label for a package, preferring the package definition,
 * then the backend-provided display name, then the raw package name. Single source
 * of truth so the table, detail, edit, and delete views never drift.
 */
export function packageDisplayLabel(pkg: {
  display_name?: string;
  package_name?: string;
  definition?: PackageDefinition;
}) {
  return pkg.definition?.display_name || pkg.display_name || pkg.package_name || '-';
}

/** Border-only panel used inside dialogs to frame read-only package details. */
export const detailBoxSx = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  p: 1.5,
  display: 'grid',
  gap: 1,
} as const;

export function packageFileLabel(pkg: Package) {
  return pkg.file_name || '-';
}

export function formatToken(value: string) {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

/** Parses the 409 body returned when a package is still referenced by rollouts. */
export function packageInUseError(err: unknown): PackageInUseConflict | null {
  const problem = getAPIProblem(err);
  if (!problem || problem.code !== API_ERROR_CODES.packageInUse) return null;
  const blockingRollouts = problem.details.blocking_rollouts;
  if (!Array.isArray(blockingRollouts)) return null;
  return {
    detail: problem.detail,
    blocking_rollouts: blockingRollouts as PackageInUseConflict['blocking_rollouts'],
  };
}

export function extractPackageError(err: unknown, fallback: string) {
  return getAPIErrorMessage(err, fallback);
}
